/**
 * RestoID — the terms sentence under a landing's CTA, and the inline policy link
 * it is built from.
 * ---------------------------------------------------------------------------
 * The identifier landings' stand-in for upstream's `TermsAndPrivacyLinks` footer.
 * It lived twice — privately in `custom/pages/SignIn.tsx` and, exported as
 * `LandingTerms`, in `custom/pages/IdentifierSignIn.tsx` — and the two copies were
 * identical. This is the one copy: legal wording that is stated once cannot drift
 * between the screens that are legally the same landing.
 *
 * It lives here rather than in `custom/components/restoid.tsx` because that module
 * is the design-source port and imports neither `useTerms`, `useCopy`/`useLang`, nor
 * the router.
 */
import { type ReactNode, useState } from 'react';

import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useTerms from '@/hooks/use-terms';

import { type Lang, useCopy, useLang } from '../copy';

import { C, FONT } from './restoid';

/** Quote marks per locale, so a substituted button name still reads correctly. */
const QUOTES: Record<Lang, readonly [string, string]> = {
  ru: ['«', '»'],
  en: ['“', '”'],
};

/** Inline policy link — footer-style hover (color brightens). Opens in the same
 *  tab: internal doc routes (/privacy, /terms) navigate in-app so the landing
 *  stays intact for "Back"; external policy URLs follow the anchor. */
export const PolicyLink = ({ href, children }: { href: string; children: ReactNode }) => {
  const [hov, setHov] = useState(false);
  const navigate = useNavigateWithPreservedSearchParams();
  const isInternal = href.startsWith('/');
  return (
    <a
      href={href}
      onClick={(event) => {
        if (
          isInternal &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
          navigate(href);
        }
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        color: hov ? C.text : C.sub,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color .15s ease',
      }}
    >
      {children}
    </a>
  );
};

/**
 * RestoID terms sentence — centred, with the policy phrases as footer-style links.
 * The sentence NAMES the button, so it has to follow whichever CTA it sits under:
 * on a username form it would otherwise still read "«Получить код»".
 * `action` omitted keeps the phone wording verbatim from the design source.
 */
export const Terms = ({ action }: { action?: string }) => {
  const c = useCopy();
  const lang = useLang();
  const { termsOfUseUrl, privacyPolicyUrl } = useTerms();
  // Fall back to the same routes the footer links use when no policy URL is configured.
  const privacyHref = privacyPolicyUrl ?? '/privacy';
  const dataHref = termsOfUseUrl ?? '/terms';
  const [open, close] = QUOTES[lang];

  return (
    <p style={{ margin: 0, textAlign: 'center', color: C.faint, font: `400 12.5px/1.6 ${FONT}` }}>
      {c('termsPre')}
      <span style={{ color: C.sub }}>
        {action === undefined ? c('termsBtn') : `${open}${action}${close}`}
      </span>
      {c('termsMid')}
      <PolicyLink href={privacyHref}>{c('termsPriv')}</PolicyLink>
      {c('termsAnd')}
      <PolicyLink href={dataHref}>{c('termsData')}</PolicyLink>.
    </p>
  );
};
